import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FaBars } from 'react-icons/fa';
import { Icon } from '../common/Icon';
import { authService } from '../../services/auth';
import { useUser, useCoins } from '../../contexts/UserContext';
import SignInModal from '../auth/SignInModal';
import ProfileDropdown from '../common/ProfileDropdown';
import FeedbackForm from '../common/FeedbackForm';
import { config } from '../../config';

const HeaderContainer = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background-color: rgb(31, 31, 31);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  cursor: pointer;
  transition: opacity 0.2s;
  

  &:hover {
    opacity: 0.8;
  }
`;

const LogoImage = styled.img`
  width: 72px;
  height: 72px;
  object-fit: contain;
`;

const Nav = styled.nav`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  background-color: white;
  color: rgb(31, 31, 31);
  cursor: pointer;
  font-size: 1rem;
  font-weight: 500;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  display: none;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const CoinDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #fbbf24;
  font-weight: 600;
  font-size: 1rem;
  margin-right: 1rem;
`;

const CoinText = styled.span`
  color: white;
`;

const CoinImage = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
`;

interface HeaderProps {
  onMenuClick: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, isLoggedIn, setIsLoggedIn } = useUser();
  const { totalCoins } = useCoins();
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignIn = () => {
    setIsLoggedIn(true);
    setIsSignInModalOpen(false);
  };

  const handleSignOut = async () => {
    await authService.logout();
    setIsLoggedIn(false);
    navigate('/');
  };

  const handleSettings = () => {
    navigate('/you?tab=settings');
  };

  const handleMyScripts = () => {
    navigate('/you?tab=scripts');
  };

  const handleFavorites = () => {
    navigate('/you?tab=favorites');
  };

  const handleSendFeedback = () => {
    setIsFeedbackFormOpen(true);
  };

  const handleSubscription = () => {
    navigate('/subscription');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <>
    <HeaderContainer>
        <Logo onClick={handleLogoClick}>
          <LogoImage src="/images/logo72.png" alt="YouMeditation Logo" />
          YouMeditation
        </Logo>
        <Nav>
      <MenuButton onClick={onMenuClick}>
            <Icon icon={FaBars} size={24} />
      </MenuButton>
          {isLoggedIn ? (
          <>
              {isLoggedIn && totalCoins !== undefined && (
                <CoinDisplay>
                  <CoinText>{totalCoins.toLocaleString()}</CoinText>
                  <CoinImage src="/images/YMcoin.png" alt="YouMeditation Coin" />
                </CoinDisplay>
              )}
              <ProfileDropdown
                profileImage={user?.profile_image?`https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${user?.profile_image}`:undefined}
                onSignOut={handleSignOut}
                onSettings={handleSettings}
                onMyScripts={handleMyScripts}
                onFavorites={handleFavorites}
                onSendFeedback={handleSendFeedback}
                onSubscription={handleSubscription}
              />
          </>
        ) : (
            <Button onClick={() => setIsSignInModalOpen(true)}>Sign In</Button>
        )}
        </Nav>
    </HeaderContainer>
      <SignInModal
        isOpen={isSignInModalOpen}
        onClose={() => setIsSignInModalOpen(false)}
        onSuccess={handleSignIn}
      />
      <FeedbackForm
        isOpen={isFeedbackFormOpen}
        onClose={() => setIsFeedbackFormOpen(false)}
      />
    </>
  );
};

export default Header; 